import React from "react";
import { BrowserRouter as Router, Switch } from "react-router-dom";
import Routes from "./routes/Routes";
import { getCustomer, updateCliente } from "api";
import { CLIENTE } from "api/TYPES";
import { useMutation, useQuery } from "react-query";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { GoogleMapsProvider } from "pages/Tracking/GoogleMapsManager"; // Import the provider
import "./App.css";

require("analytics");

function App() {
  const { data: dataCliente } = useQuery(CLIENTE.name, getCustomer);
  const [rfcSaved, setRfcSaved] = React.useState(false);
  const cliente = dataCliente?.cliente;

  return (
    <GoogleMapsProvider> {/* Wrap the entire app with GoogleMapsProvider */}
      <RfcRequiredDialog cliente={cliente} rfcSaved={rfcSaved} onSaved={() => setRfcSaved(true)} />
      <Router>
        <Switch>
          <Routes />
        </Switch>
      </Router>
    </GoogleMapsProvider>
  );
}

export default App;

function RfcRequiredDialog({ cliente, rfcSaved, onSaved }) {
  const [rfcs, setRfcs] = React.useState({});
  const [errors, setErrors] = React.useState({});
  const { mutateAsync: saveRfcs, status, error: saveError } = useMutation(updateCliente);

  const empresas = React.useMemo(
    () => cliente?.empresas_seguro_paqueteria || [],
    [cliente?.empresas_seguro_paqueteria]
  );
  const missingEmpresas = React.useMemo(
    () =>
      empresas
        .map((empresa, index) => ({ empresa, index }))
        .filter(({ empresa }) => !empresa?.rfc?.trim()),
    [empresas]
  );
  const shouldAskForRfc =
    cliente?.status === "activo" &&
    cliente?.seguro_paqueteria &&
    missingEmpresas.length > 0 &&
    !rfcSaved;

  React.useEffect(() => {
    setRfcs({});
    setErrors({});
  }, [cliente?._id]);

  const handleChange = (index) => (event) => {
    const value = event.target.value.toUpperCase();
    setErrors((prev) => ({ ...prev, [index]: "" }));
    setRfcs((prev) => ({ ...prev, [index]: value }));
  };

  const handleSave = async () => {
    const nextRfcs = {};
    const nextErrors = {};
    missingEmpresas.forEach(({ index }) => {
      const value = rfcs[index]?.trim().toUpperCase();
      if (!value) {
        nextErrors[index] = "Ingresa el RFC para continuar.";
      } else {
        nextRfcs[index] = value;
      }
    });

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const updatedEmpresas = empresas.map((empresa, index) =>
      nextRfcs[index] !== undefined ? { ...empresa, rfc: nextRfcs[index] } : empresa
    );

    try {
      await saveRfcs({ empresas_seguro_paqueteria: updatedEmpresas });
      onSaved();
    } catch (e) {
      console.error(e);
    }
  };

  const isMultiple = missingEmpresas.length > 1;

  return (
    <Dialog open={shouldAskForRfc} fullWidth maxWidth="sm" disableBackdropClick disableEscapeKeyDown>
      <DialogTitle>{isMultiple ? "Agrega los RFC de tus empresas" : "Agrega el RFC de tu empresa"}</DialogTitle>
      <DialogContent>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            Para continuar, necesitamos que agregues el RFC de{" "}
            {isMultiple ? "las siguientes empresas aseguradas" : "la siguiente empresa asegurada"} en paqueteria.
          </div>
          {missingEmpresas.map(({ empresa, index }, position) => {
            const razonSocial = empresa?.razon_social || `Empresa ${index + 1}`;
            return (
              <TextField
                key={index}
                autoFocus={position === 0}
                fullWidth
                label={`RFC de ${razonSocial}`}
                value={rfcs[index] || ""}
                onChange={handleChange(index)}
                error={!!errors[index]}
                helperText={errors[index] || `Escribe el RFC correspondiente a ${razonSocial}.`}
                inputProps={{ maxLength: 13 }}
                variant="outlined"
              />
            );
          })}
          {saveError && <Alert severity="error">{saveError.errmsg || "No se pudo guardar el RFC."}</Alert>}
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={status === "loading"}
        >
          {status === "loading" ? <CircularProgress size={18} style={{ color: "white" }} /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}